import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { apiService } from 'utils/api';
import OrderLoading from './order-loading';
import CancelTripModal from 'components/live-trip-cards/live-trip-modals/cancel-trip-modal';
import CompleteTripModal from 'components/live-trip-cards/live-trip-modals/complete-trip-modal';
import { SocketIOConnection } from 'utils/socket_client';
import { OrderType } from 'models/order.model';
import OrderNotFound from 'components/base/order-not-found';
import { STORAGE_KEYS } from 'utils/constants';
import CSOrderDetails from './cs-order-details';
import KitchenOrderDetails from './kitchen-order-details';
import LogisticsOrderDetails from './logistics-order-details';
import { getOrderStatus, isCSRole } from 'utils/functions';
import { Button } from '@cokitchen/cokitchen-components';
import { ENUM_CS_ROLE, ENUM_KITCHEN_ROLE, ENUM_ROLE } from '@cokitchen/cokitchen-auth';
import { ENUM_STATUS } from 'utils/enum';
import { useSearchParams } from 'react-router-dom';

const getOrderApi = (order_code: string) =>
  apiService.get(`orders/code/${order_code}`, {
    baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY
  });

type Props = {
  orderCode: string;
};

const OrderDetail: FC<Props> = ({ orderCode }) => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<OrderType>();
  const [showChangeAddressModal, setShowChangeAddressModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  if (!orderCode) return <OrderNotFound />;

  const addressModal = (withRes?: boolean) => {
    if (withRes) getOrder();
    setShowChangeAddressModal(!showChangeAddressModal);
  };

  const updateOrderAndStatus = (orderData: OrderType) => {
    setOrder(orderData);

    searchParams.set('orderStatus', getOrderStatus(orderData));
    setSearchParams(searchParams);
  };

  const getOrder = async () => {
    try {
      const res = await getOrderApi(orderCode);

      const orderData = res.data?.data;

      if (res.status != 200 || orderData.id == undefined) {
        setNotFound(true);
      }
      updateOrderAndStatus(orderData);
      connectSocket(orderData.id);
    } finally {
      setLoading(false);
    }
  };

  const connectSocket = async (orderId: string) => {
    const conn = await SocketIOConnection.getConnection();
    conn.onReconnect(async () => {
      conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
      const res = await getOrderApi(orderCode);
      updateOrderAndStatus(res.data?.data);
    });
    const roomName = `order:${orderId}`;
    conn.addEventListener(roomName, 'updated', (data: any) => {
      updateOrderAndStatus({ ...order, ...data });
    });
    conn.joinRoom(roomName, process.env.REACT_APP_WEBOCKETAPP_KEY);
  };

  useEffect(() => {
    getOrder();
  }, []);

  const canTakeAction = useMemo(() => {
    const isActionValid =
      order && ![ENUM_STATUS.COMPLETED, ENUM_STATUS.CANCELLED].includes(getOrderStatus(order));

    return isActionValid;
  }, [order]);

  const DetailsComponent = useMemo(() => {
    const storeRole = localStorage.getItem(STORAGE_KEYS.ROLE) as ENUM_ROLE;

    if (Object.values<string>(ENUM_CS_ROLE).includes(storeRole)) return CSOrderDetails;

    if (Object.values<string>(ENUM_KITCHEN_ROLE).includes(storeRole)) return KitchenOrderDetails;

    return LogisticsOrderDetails;
  }, []);

  if (loading) return <OrderLoading />;

  if (notFound || !order || !order?.calculated_order) return <OrderNotFound />;

  return (
    <Fragment>
      <div className='flex flex-col gap-4'>
        <DetailsComponent
          addressModal={addressModal}
          order={order}
          showChangeAddressModal={showChangeAddressModal}
        />

        {canTakeAction && (
          <div className='flex flex-col-reverse md:flex-row justify-center gap-4'>
            {isCSRole() ? (
              <Button
                onClick={() => setShowCancelModal(true)}
                small
                color='primary'
                variant='outline'>
                Cancel trip
              </Button>
            ) : (
              <Button
                small
                onClick={() => setShowCompleteModal(true)}
                color='primary'
                variant='block'
                loading={loading}>
                Complete Trip
              </Button>
            )}
          </div>
        )}
      </div>

      {showCompleteModal && (
        <CompleteTripModal
          show={showCompleteModal}
          onClose={() => setShowCompleteModal(false)}
          order={order}
        />
      )}

      <CancelTripModal
        show={showCancelModal}
        order={order}
        onClose={() => setShowCancelModal(false)}
      />
    </Fragment>
  );
};

export default OrderDetail;
